'use client'

import { Button } from '@/ui/atoms/button/Button'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { AlertTriangle, RefreshCw, Wallet } from 'lucide-react'
import { type Ref } from 'react'
import { Address } from 'viem'
import { ATokenApprovalParams, useATokenApproval } from '../hooks/useATokenApproval'
import { formatApprovalError } from '../utils/formatApprovalError'

interface SpendAllowanceStatusProps {
  approvalParams: ATokenApprovalParams
  tokenAddress?: Address
  /**
   * Optional ref forwarded to the initial-approval "Set to X" button shown
   * when `needsApproval` is true. Lets a parent CTA (e.g. "Complete Setup"
   * in CardDashboard) trigger the same approval via `.click()` without
   * spawning a second `useSendTransaction` instance.
   */
  approveButtonRef?: Ref<HTMLButtonElement>
}

export function SpendAllowanceStatus({ approvalParams, tokenAddress, approveButtonRef }: SpendAllowanceStatusProps) {
  const {
    needsApproval,
    isLoadingAllowance,
    isAllowanceLoaded,
    approveAToken,
    isApproving,
    isConfirming,
    approveError,
    resetError,
    remainingAllowance,
    requiredLimit,
    allowancePercentage,
    isCriticallyLow,
    tokenSymbol,
    refetchAllowance,
    brahmaAddress,
  } = useATokenApproval({
    tokenAddress,
    requiredLimit: approvalParams.creditLimit,
  })

  const handleTopUp = async () => {
    resetError()
    try {
      await approveAToken(approvalParams)
    } catch (error) {
      console.error('Error approving aToken:', error)
    }
  }

  if (!isAllowanceLoaded || isLoadingAllowance) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
        <Typography variant="p" className="text-muted-foreground text-sm">
          Checking spend allowance…
        </Typography>
      </div>
    )
  }

  const isZero = remainingAllowance === 0
  const isHealthy = !needsApproval && allowancePercentage >= 50

  const statusColor = isZero
    ? 'text-red-400'
    : needsApproval || isCriticallyLow
      ? 'text-red-400'
      : isHealthy
        ? 'text-green-400'
        : 'text-yellow-400'
  const barColor = isZero
    ? 'bg-red-500'
    : needsApproval || isCriticallyLow
      ? 'bg-red-500'
      : isHealthy
        ? 'bg-green-500'
        : 'bg-yellow-500'
  const borderColor = isZero
    ? 'border-red-500/30'
    : needsApproval || isCriticallyLow
      ? 'border-red-500/30'
      : isHealthy
        ? 'border-border/50'
        : 'border-yellow-500/30'
  const bgColor = isZero
    ? 'bg-red-500/5'
    : needsApproval || isCriticallyLow
      ? 'bg-red-500/5'
      : isHealthy
        ? 'bg-muted/20'
        : 'bg-yellow-500/5'

  if (needsApproval) {
    const iconColor = isZero ? 'text-red-400' : 'text-amber-400'
    const textColor = isZero ? 'text-red-300' : 'text-amber-300'
    const subtleTextColor = isZero ? 'text-red-200/60' : 'text-amber-200/60'
    const bgBarColor = isZero ? 'bg-red-500' : 'bg-amber-500'
    const criticalBorderColor = isZero ? 'border-red-500/30' : 'border-amber-500/30'
    const criticalBgColor = isZero ? 'bg-red-500/5' : 'bg-amber-500/5'

    return (
      <div className={cn('space-y-3 rounded-lg border px-3 py-3', criticalBorderColor, criticalBgColor)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isZero ? (
              <AlertTriangle className="h-4 w-4 text-red-400" />
            ) : (
              <Wallet className={cn('h-4 w-4', iconColor)} />
            )}
            <Typography variant="p" className={cn('font-medium text-sm', textColor)}>
              {isZero ? 'Spend Allowance Required' : 'Spend Allowance Low'}
            </Typography>
          </div>
          <Button
            ref={approveButtonRef}
            size="sm"
            onClick={handleTopUp}
            disabled={isApproving || isConfirming}
            className="h-7 bg-gradient-to-r from-primary to-primary/80 px-3 text-xs"
          >
            {isApproving || isConfirming ? (
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/20 border-t-white" />
            ) : (
              `Set to ${requiredLimit.toLocaleString()} ${tokenSymbol}`
            )}
          </Button>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <Typography variant="p" className="text-muted-foreground">
              Remaining allowance
            </Typography>
            <Typography variant="p" className={cn('font-medium', textColor)}>
              {remainingAllowance.toLocaleString(undefined, { maximumFractionDigits: 0 })} of{' '}
              {requiredLimit.toLocaleString()} {tokenSymbol}
            </Typography>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn('h-full rounded-full transition-all duration-500', bgBarColor)}
              style={{ width: `${allowancePercentage}%` }}
            />
          </div>
        </div>

        <Typography variant="p" className={cn('text-xs', subtleTextColor)}>
          {isZero
            ? 'Approve a spend allowance to use your card.'
            : `Only ${remainingAllowance.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${tokenSymbol} left — reset to ${requiredLimit.toLocaleString()} to keep spending.`}
        </Typography>
        {approveError && (
          <Typography variant="p" className="text-red-400 text-xs">
            {formatApprovalError(approveError, 'Approval failed.')}
          </Typography>
        )}
        {brahmaAddress && (
          <Typography
            variant="p"
            className={cn('font-mono text-[11px]', isZero ? 'text-red-300/70' : 'text-amber-300/70')}
          >
            Spender {brahmaAddress.slice(0, 6)}...{brahmaAddress.slice(-4)}
          </Typography>
        )}
      </div>
    )
  }

  return (
    <div className={cn('space-y-2 rounded-lg border px-3 py-2.5', borderColor, bgColor)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet className={cn('h-4 w-4', statusColor)} />
          <Typography variant="p" className="font-medium text-sm">
            Spend Allowance
          </Typography>
        </div>
        <div className="flex items-center gap-2">
          <Typography variant="p" className={cn('font-semibold text-sm', statusColor)}>
            {remainingAllowance.toLocaleString(undefined, { maximumFractionDigits: 0 })} {tokenSymbol}
          </Typography>
          <Typography variant="p" className="text-muted-foreground text-xs">
            / {requiredLimit.toLocaleString()}
          </Typography>
          <button
            onClick={() => refetchAllowance()}
            className="text-muted-foreground transition-colors hover:text-foreground"
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn('h-full rounded-full transition-all duration-500', barColor)}
              style={{ width: `${Math.min(100, allowancePercentage)}%` }}
            />
          </div>
        </div>
        <Typography variant="p" className="w-8 text-right text-muted-foreground text-xs">
          {allowancePercentage.toFixed(0)}%
        </Typography>
        {Math.abs(remainingAllowance - requiredLimit) > 0.5 && (
          <Button
            size="sm"
            variant="secondary"
            onClick={handleTopUp}
            disabled={isApproving || isConfirming}
            className={cn(
              'h-7 px-2.5 text-xs',
              isCriticallyLow
                ? 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground',
            )}
          >
            {isApproving || isConfirming ? (
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/20 border-t-white" />
            ) : (
              'Reset'
            )}
          </Button>
        )}
      </div>

      {isCriticallyLow && (
        <Typography variant="p" className="text-red-300/80 text-xs">
          Running low — reset allowance to keep spending.
        </Typography>
      )}
      {approveError && (
        <Typography variant="p" className="text-red-400 text-xs">
          {formatApprovalError(approveError, 'Failed.')}
        </Typography>
      )}
      {brahmaAddress && (
        <Typography variant="p" className="font-mono text-[11px] text-muted-foreground/80">
          Spender {brahmaAddress.slice(0, 6)}...{brahmaAddress.slice(-4)}
        </Typography>
      )}
    </div>
  )
}
